import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { adminApi } from "@/lib/api";
import { RefreshCw, Flag, ExternalLink, CheckCheck, SearchCheck, XCircle, Undo2 } from "lucide-react";

/** Phase 10.7 — Admin corrections triage. Lifecycle: new -> reviewing -> confirmed -> corrected | dismissed. */

const ST_CLS = {
  new: "bg-amber-50 text-amber-900 border-amber-300",
  reviewing: "bg-sky-50 text-sky-800 border-sky-200",
  confirmed: "bg-violet-50 text-violet-800 border-violet-200",
  corrected: "bg-emerald-50 text-emerald-800 border-emerald-200",
  dismissed: "bg-slate-100 text-slate-500 border-slate-200",
};

const NoteAction = ({ row, status, label, icon: Icon, onDone, requireNote }) => {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const act = async () => {
    try {
      await adminApi.patch(`/admin/corrections/${row.id}`, {
        status, resolution_note: note.trim() || null,
      });
      toast.success(`Report marked ${status}.`);
      setOpen(false); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed."); }
  };
  if (!requireNote) {
    return (
      <Button size="sm" variant="outline" onClick={act} data-testid={`correction-${status}-${row.id}`}>
        <Icon className="h-3.5 w-3.5 mr-1" /> {label}
      </Button>
    );
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`correction-${status}-${row.id}`}>
          <Icon className="h-3.5 w-3.5 mr-1" /> {label}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader>
          <DialogTitle>{label}</DialogTitle>
          <DialogDescription>A resolution note is required, recorded in the audit trail, and
            SHOWN PUBLICLY on the reporter's status page — write it for public consumption and never
            include reporter identity or internal details.</DialogDescription>
        </DialogHeader>
        <Label className="text-xs">Resolution note (public)</Label>
        <Textarea className="mt-1" value={note} maxLength={2000}
                  onChange={(e) => setNote(e.target.value)} data-testid="correction-note" />
        <Button className="bg-primary hover:bg-primary/90 w-full" onClick={act} data-testid="correction-note-confirm">
          Confirm
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default function Corrections() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(() => {
    const qs = filter === "all" ? "" : `?status=${filter}`;
    adminApi.get(`/admin/corrections${qs}`).then((r) => setData(r.data)).catch(() => setData({ items: [], counts: {} }));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-corrections-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="font-heading font-bold text-2xl text-foreground inline-flex items-center gap-2">
            <Flag className="h-6 w-6 text-accent" /> Corrections
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            Public error reports. Workflow: Review → Confirm → Correct (or Dismiss) — every step is audited.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={load} data-testid="corrections-refresh">
          <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
        </Button>
      </div>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="flex-wrap h-auto">
          {["all", "new", "reviewing", "confirmed", "corrected", "dismissed"].map((s) => (
            <TabsTrigger key={s} value={s} data-testid={`corrections-filter-${s}`}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
              {data?.counts && s !== "all" ? ` (${data.counts[s] || 0})` : ""}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="space-y-3 mt-4">
        {data === null && <Skeleton className="h-48" />}
        {data !== null && data.items.length === 0 && (
          <Card><CardContent className="p-6 text-sm text-muted-foreground" data-testid="corrections-empty">
            No reports {filter !== "all" ? `with status “${filter}”` : "yet"}.
          </CardContent></Card>
        )}
        {data !== null && data.items.map((row) => (
          <Card key={row.id} data-testid={`correction-row-${row.id}`}>
            <CardContent className="p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className={`${ST_CLS[row.status] || ""} text-[10px] uppercase`}>{row.status}</Badge>
                    {row.public_code && (
                      <span className="font-mono text-[10px] text-muted-foreground" data-testid="correction-public-code">{row.public_code}</span>
                    )}
                    <Badge variant="outline" className="bg-secondary text-foreground border-slate-200 text-[10px]">
                      {row.category_label}
                    </Badge>
                    {row.is_test && (
                      <Badge variant="outline" className="bg-amber-50 text-amber-900 border-amber-300 text-[10px]">TEST</Badge>
                    )}
                    <span className="text-[10px] text-muted-foreground">{new Date(row.created_at).toLocaleString()}</span>
                  </div>
                  <p className="text-sm text-foreground mt-2 whitespace-pre-wrap">{row.description}</p>
                  {row.suggested_correction && (
                    <p className="text-xs text-muted-foreground mt-1.5"><span className="font-semibold">Suggested:</span> {row.suggested_correction}</p>
                  )}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] text-muted-foreground">
                    {row.page_url && <span>Page: <code className="font-mono">{row.page_url}</code></span>}
                    {row.source_url && (
                      <a href={row.source_url} target="_blank" rel="noopener noreferrer"
                         className="text-primary underline underline-offset-2 inline-flex items-center gap-0.5">
                        Reporter's source <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                    {row.reporter_email && <span>Contact: {row.reporter_email}</span>}
                  </div>
                  {row.resolution_note && (
                    <p className="text-xs text-foreground mt-2 rounded bg-secondary p-2">
                      <span className="font-semibold">Resolution:</span> {row.resolution_note}
                    </p>
                  )}
                  {row.history?.length > 1 && (
                    <p className="text-[10px] text-muted-foreground mt-2 font-mono">
                      {row.history.map((h) => `${h.at.slice(0, 16)} ${h.action}${h.by !== "public" ? " · " + h.by : ""}`).join(" → ")}
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {row.status === "new" && (
                    <NoteAction row={row} status="reviewing" label="Start review" icon={SearchCheck} onDone={load} />
                  )}
                  {row.status === "reviewing" && (
                    <NoteAction row={row} status="confirmed" label="Confirm error" icon={CheckCheck} onDone={load} />
                  )}
                  {row.status === "confirmed" && (
                    <NoteAction row={row} status="corrected" label="Mark corrected" icon={CheckCheck} onDone={load} requireNote />
                  )}
                  {["new", "reviewing", "confirmed"].includes(row.status) && (
                    <NoteAction row={row} status="dismissed" label="Dismiss" icon={XCircle} onDone={load} requireNote />
                  )}
                  {row.status === "dismissed" && (
                    <NoteAction row={row} status="reviewing" label="Reopen" icon={Undo2} onDone={load} />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
